/**
 * Formats d'affichage — français marocain : montants `1 250,00 DH`, dates
 * `jj/mm/aaaa`. Une seule source pour toute l'application, PDF compris.
 */

const ESPACE_FINE = ' ';

/** `1250.5` -> `1 250,00 DH`. */
export function dh(montant: number | null | undefined, options: { signe?: boolean } = {}): string {
  const v = typeof montant === 'number' && Number.isFinite(montant) ? montant : 0;
  const prefixe = options.signe && v > 0 ? '+' : '';
  return `${prefixe}${nombre(v)}${ESPACE_FINE}DH`;
}

/**
 * `1250.5` -> `1 250,00` (sans devise, pour les colonnes de tableau).
 *
 * Le séparateur de milliers est forcé à l'espace : selon la version d'ICU,
 * `fr-MA` groupe avec un point (`1.250,00`), ce qui n'est pas la convention
 * retenue pour les documents du magasin.
 */
export function nombre(valeur: number | null | undefined, decimales = 2): string {
  const v = typeof valeur === 'number' && Number.isFinite(valeur) ? valeur : 0;
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  })
    .format(v)
    // fr-FR groupe avec une espace fine insécable (U+202F) ou insécable (U+00A0).
    .replace(/[  ]/g, ESPACE_FINE);
}

/** Entier sans décimale. */
export function entier(valeur: number | null | undefined): string {
  return nombre(valeur, 0);
}

/** `2026-03-12` -> `12/03/2026`. */
export function date(iso: string | null | undefined): string {
  if (!iso) return '—';
  const jour = iso.slice(0, 10);
  const [a, m, j] = jour.split('-');
  if (!a || !m || !j) return iso;
  return `${j}/${m}/${a}`;
}

/** `2026-03-12T10:30:00Z` -> `12/03/2026 à 10:30`. */
export function dateHeure(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return date(iso);
  return `${date(iso)} à ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Date du jour au format attendu par les champs `<input type="date">`. */
export function aujourdhui(): string {
  const d = new Date();
  const decalage = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - decalage).toISOString().slice(0, 10);
}

/** « il y a 3 jours », pour les listes d'activité récente. */
export function depuis(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';

  const secondes = Math.round((Date.now() - d.getTime()) / 1000);
  if (secondes < 60) return "à l'instant";
  if (secondes < 3600) return `il y a ${Math.floor(secondes / 60)} min`;
  if (secondes < 86_400) return `il y a ${Math.floor(secondes / 3600)} h`;

  const jours = Math.floor(secondes / 86_400);
  if (jours === 1) return 'hier';
  if (jours < 31) return `il y a ${jours} jours`;
  return date(iso);
}

/** Notation d'ordonnance reconstituée : `180'-0,25/-0,75`. */
export function ordonnance(oeil: {
  axe: string | null;
  sph: string | null;
  cyl: string | null;
}): string {
  if (!oeil.sph && !oeil.cyl && !oeil.axe) return '—';
  return `${oeil.axe ? `${oeil.axe}'` : ''}${oeil.sph ?? ''}${oeil.cyl ? `/${oeil.cyl}` : ''}`;
}

/** Téléphone marocain groupé : `06 61 49 41 31`. */
export function telephone(tel: string | null | undefined): string {
  if (!tel) return '—';
  const chiffres = tel.replace(/\D/g, '');
  if (chiffres.length !== 10) return tel;
  return chiffres.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
}

/**
 * Lien `tel:` composable, ou `null` si le champ n'est pas un numéro.
 *
 * Le champ téléphone du cahier a servi de fourre-tout : sur les six mille
 * fiches reprises, un millier portent deux numéros séparés par une barre, un
 * nom de proche, ou une note. Fabriquer un lien avec ça donnerait un bouton
 * qui ne compose rien — pire qu'un texte simple, parce qu'il promet d'appeler.
 *
 * Deux formes seulement sont admises : dix chiffres marocains, ou l'indicatif
 * 212 suivi de neuf. Tout le reste s'affiche tel quel, sans lien.
 */
export function lienTelephone(tel: string | null | undefined): string | null {
  if (!tel) return null;
  const chiffres = tel.replace(/\D/g, '');

  if (chiffres.length === 10 && chiffres.startsWith('0')) return `tel:${chiffres}`;
  if (chiffres.length === 12 && chiffres.startsWith('212')) return `tel:+${chiffres}`;
  return null;
}

/** Coupe proprement un texte trop long pour une cellule. */
export function tronquer(texte: string | null | undefined, longueur = 40): string {
  if (!texte) return '—';
  return texte.length > longueur ? `${texte.slice(0, longueur - 1)}…` : texte;
}

/**
 * `pluriel(2, 'commande')` -> « 2 commandes ».
 *
 * Le pluriel par défaut accorde **chaque mot** du libellé : « commande
 * importée » donne « commandes importées », et non « commande importées ».
 * Les accords irréguliers passent par le troisième argument.
 */
const MOTS_INVARIABLES = new Set(['non', 'de', 'du', 'des', 'à', 'en', 'sans', 'sur', 'a']);

export function pluriel(n: number, singulier: string, plurielForme?: string): string {
  if (n <= 1) return `${entier(n)} ${singulier}`;

  const accorde =
    plurielForme ??
    singulier
      .split(' ')
      .map((mot) =>
        MOTS_INVARIABLES.has(mot.toLowerCase()) || mot.endsWith('s') ? mot : `${mot}s`,
      )
      .join(' ');

  return `${entier(n)} ${accorde}`;
}
